import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import BudgetCalculator from "../components/BudgetCalculator";
import CurrencyConverter from "../components/CurrencyConverter";
import { fetchBudgets, fetchCities } from "../lib/api";
import { useT } from "../i18n/I18nProvider";
import { NumbeoAttribution, NumbeoCategoryGrid } from "../components/NumbeoSections";

// Stacked-bar comparative chart: 3 representative PL cities + 4 francophone references.
// Kept as-is per editorial decision D4 — the budgets seed is still the source here.
const COMPARE_DEFAULT = ["varsovie", "cracovie", "wroclaw", "paris", "bruxelles", "geneve", "montreal"];

export default function CostOfLiving() {
  const { t, lp } = useT();
  const [budgets, setBudgets] = useState([]);
  const [polishCities, setPolishCities] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("varsovie");

  useEffect(() => {
    // Stacked-bar source: legacy budgets seed (D4)
    fetchBudgets(COMPARE_DEFAULT.join(","))
      .then(setBudgets)
      .catch((e) => console.error("[COL] budgets failed:", e?.message || e));
    // Numbeo source: full city objects (returns 7 Polish cities)
    fetchCities()
      .then((data) => setPolishCities(data || []))
      .catch((e) => console.error("[COL] cities failed:", e?.message || e));
  }, []);

  const housingLabel = t("col.legendHousing");
  const lifeLabel = t("col.legendLife");
  const chartData = useMemo(() => budgets.map((b) => ({
    name: b.city_name,
    [housingLabel]: b.rent,
    [lifeLabel]: b.food + b.transport + b.utilities + b.leisure + b.other,
  })), [budgets, housingLabel, lifeLabel]);

  const selectedCity = useMemo(
    () => polishCities.find((c) => c.slug === selectedSlug) || null,
    [polishCities, selectedSlug],
  );
  const selectedNumbeo = selectedCity?.numbeo || null;
  const selectedIndicators = selectedNumbeo?.indicators || null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="col-page">
      <header className="max-w-3xl mb-10">
        <span className="hej-chip"><span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>{t("col.chip")}</span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">{t("col.title")}</h1>
        <p className="text-slate-600 mt-4 leading-relaxed">{t("col.intro")}</p>
        <p className="text-slate-600 mt-4 leading-relaxed">
          Pour mettre ces prix en face des revenus locaux, notre page{" "}
          <Link to={lp("/travailler-pologne")} className="text-[#0056D2] hover:underline">Travailler en Pologne</Link>{" "}
          donne le salaire minimum, le salaire moyen et le passage du brut au net.
        </p>
      </header>

      {/* Stacked-bar chart — D4 (unchanged: keeps legacy budgets seed for PL+FR comparison) */}
      <section className="mb-14">
        <h2 className="font-display text-2xl font-semibold text-slate-900 mb-5">{t("col.compareTitle")}</h2>
        <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="comparison-chart">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="name" fontSize={12} tick={{ fill: "#475569" }} />
                <YAxis fontSize={11} tick={{ fill: "#94a3b8" }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13 }}
                  formatter={(v) => v.toLocaleString("fr-FR") + " zł"}
                />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                <Bar dataKey={housingLabel} stackId="a" fill="#0056D2" />
                <Bar dataKey={lifeLabel} stackId="a" fill="#DC143C" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-slate-500 mt-3 text-center">{t("col.compareSource")}</p>
        </div>
      </section>

      <section className="mb-14"><BudgetCalculator /></section>

      {/* Detail prices by city — Numbeo source, 7 PL cities toggle */}
      <section className="mb-14" data-testid="col-detail-section">
        <h2 className="font-display text-2xl font-semibold text-slate-900 mb-5">{t("col.detailTitle")}</h2>

        <div className="flex gap-2 mb-6 flex-wrap" data-testid="col-city-toggle">
          {polishCities.map((c) => (
            <button
              key={c.slug}
              onClick={() => setSelectedSlug(c.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                selectedSlug === c.slug
                  ? "bg-[#0056D2] text-white"
                  : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
              data-testid={`col-city-tab-${c.slug}`}
            >
              {c.name_fr}
            </button>
          ))}
        </div>

        {selectedIndicators ? (
          <div data-testid="col-detail" key={selectedSlug}>
            <NumbeoCategoryGrid indicators={selectedIndicators} testIdPrefix="col-category" />
            <NumbeoAttribution numbeo={selectedNumbeo} testId="col-attribution" />
          </div>
        ) : (
          <p className="text-sm text-slate-500 italic" data-testid="col-no-numbeo">{t("city.noNumbeoData")}</p>
        )}
      </section>

      <section><CurrencyConverter /></section>
    </div>
  );
}
